import React, { useState, useEffect, useRef } from 'react';
import { API_BASE_URL, fetchWithAuth } from '../config';
import './EventoEntregaSeguridad.css';

export default function RegistroMovimientoSeguridad({
    modo, // 'devolucion' | 'cambio'
    evento,
    persona,
    usuario,
    empresaId,
    onClose,
    onSuccess
}) {
    const [articulosDisponibles, setArticulosDisponibles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    const [fotoData, setFotoData] = useState(null);
    const [firmaData, setFirmaData] = useState(null);
    const canvasRef = useRef(null);
    const [isDrawing, setIsDrawing] = useState(false);

    const [form, setForm] = useState({
        id_articulo: '',
        id_articulo_nuevo: '',
        talla_anterior: '',
        talla_nueva: '',
        cantidad: 1,
        motivo: '',
        observaciones: '',
        fecha: new Date().toISOString().split('T')[0]
    });

    useEffect(() => {
        fetchArticulos();
    }, []);

    const fetchArticulos = async () => {
        try {
            setLoading(true);
            const res = await fetchWithAuth(`${API_BASE_URL}/api/articulos-seguridad?id_empresa=${empresaId}`);
            if (res.ok) {
                setArticulosDisponibles(await res.json());
            } else {
                setError('Error al cargar artículos');
            }
        } catch (err) {
            setError('Error al cargar artículos');
        } finally {
            setLoading(false);
        }
    };

    const getCanvasPoint = (event) => {
        const canvas = canvasRef.current;
        const rect = canvas.getBoundingClientRect();
        const touch = event.touches?.[0] || event.changedTouches?.[0];
        const clientX = touch ? touch.clientX : event.clientX;
        const clientY = touch ? touch.clientY : event.clientY;
        const scaleX = canvas.width / rect.width;
        const scaleY = canvas.height / rect.height;
        return {
            x: (clientX - rect.left) * scaleX,
            y: (clientY - rect.top) * scaleY
        };
    };

    const handleCanvasMouseDown = (e) => {
        e.preventDefault();
        setIsDrawing(true);
        const canvas = canvasRef.current;
        const { x, y } = getCanvasPoint(e);
        const ctx = canvas.getContext('2d');
        ctx.beginPath();
        ctx.moveTo(x, y);
    };

    const handleCanvasMouseMove = (e) => {
        if (!isDrawing) return;
        e.preventDefault();
        const canvas = canvasRef.current;
        const { x, y } = getCanvasPoint(e);
        const ctx = canvas.getContext('2d');
        ctx.lineTo(x, y);
        ctx.stroke();
    };

    const handleCanvasMouseUp = () => {
        const canvas = canvasRef.current;
        if (canvas && isDrawing) {
            const ctx = canvas.getContext('2d');
            ctx.closePath();
            setFirmaData(canvas.toDataURL());
        }
        setIsDrawing(false);
    };

    const limpiarFirma = () => {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        setFirmaData(null);
    };

    const handleSubirFoto = (e) => {
        const file = e.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (ev) => setFotoData(ev.target.result);
            reader.readAsDataURL(file);
        }
    };

    const articuloDetalle = (evento.detalles || []).find(d => d.id === form.id_articulo || d.id_articulo === form.id_articulo);
    const articuloSeleccionado = articulosDisponibles.find(a => a.id === form.id_articulo);
    const articuloNuevo = articulosDisponibles.find(a => a.id === form.id_articulo_nuevo);

    const handleArticuloCambio = (idArticulo) => {
        const det = (evento.detalles || []).find(d => d.id === idArticulo || d.id_articulo === idArticulo);
        setForm({
            ...form,
            id_articulo: idArticulo,
            talla_anterior: det?.talla_entregada || '',
            id_articulo_nuevo: '',
            talla_nueva: ''
        });
    };

    const handleSubmit = async () => {
        setError('');

        if (!form.fecha) {
            setError('La fecha es obligatoria');
            return;
        }
        if (!form.motivo.trim()) {
            setError('El motivo es obligatorio');
            return;
        }

        if (modo === 'devolucion') {
            if (!form.id_articulo) {
                setError('Seleccione un artículo a devolver');
                return;
            }
        } else {
            if (!form.id_articulo || !form.id_articulo_nuevo) {
                setError('Seleccione el artículo anterior y el nuevo');
                return;
            }
            if (form.id_articulo === form.id_articulo_nuevo) {
                setError('El artículo nuevo debe ser diferente al anterior');
                return;
            }
            if (articuloNuevo?.requiere_talla && !form.talla_nueva) {
                setError('El artículo nuevo requiere talla');
                return;
            }
        }

        try {
            setSaving(true);
            const bodyInicial = modo === 'devolucion'
                ? {
                    id_evento_entrega: evento.id,
                    id_personal: persona.id,
                    id_articulo: form.id_articulo,
                    fecha_devolucion: form.fecha,
                    motivo: form.motivo,
                    observaciones: form.observaciones,
                    recibido_por: usuario.id
                }
                : {
                    id_evento_entrega: evento.id,
                    id_personal: persona.id,
                    id_articulo_anterior: form.id_articulo,
                    id_articulo_nuevo: form.id_articulo_nuevo,
                    talla_anterior: form.talla_anterior,
                    talla_nueva: form.talla_nueva,
                    cantidad: form.cantidad,
                    fecha_cambio: form.fecha,
                    motivo: form.motivo,
                    observaciones: form.observaciones,
                    recibido_por: usuario.id
                };

            const url = modo === 'devolucion'
                ? `${API_BASE_URL}/api/devoluciones-seguridad`
                : `${API_BASE_URL}/api/cambios-seguridad`;

            const res = await fetchWithAuth(url, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(bodyInicial)
            });

            if (!res.ok) {
                const data = await res.json();
                throw new Error(data.error || 'Error al registrar');
            }

            const registro = await res.json();

            const completarUrl = modo === 'devolucion'
                ? `${API_BASE_URL}/api/devoluciones-seguridad/${registro.id}/completar`
                : `${API_BASE_URL}/api/cambios-seguridad/${registro.id}/completar`;

            const completarRes = await fetchWithAuth(completarUrl, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    [modo === 'devolucion' ? 'foto_devolucion' : 'foto_cambio']: fotoData,
                    [modo === 'devolucion' ? 'firma_devolucion' : 'firma_cambio']: firmaData
                })
            });

            if (!completarRes.ok) {
                throw new Error('Registro creado, pero error al guardar foto/firma');
            }

            setSuccess(modo === 'devolucion' ? 'Devolución registrada exitosamente' : 'Cambio de artículo registrado exitosamente');
            setTimeout(() => {
                if (onSuccess) onSuccess();
                if (onClose) onClose();
            }, 1200);
        } catch (err) {
            setError(err.message || 'Error al registrar movimiento');
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="loading-state"><p>Cargando...</p></div>;

    const titulo = modo === 'devolucion' ? '📦 Registrar Devolución' : '🔄 Registrar Cambio de Artículo';

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-content evento-entrega-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                    <h2>{titulo}</h2>
                    <button className="close-btn" onClick={onClose}>×</button>
                </div>

                <div className="modal-body">
                    {error && <div className="error-message">{error}</div>}
                    {success && <div className="success-message">{success}</div>}

                    <p className="step-subtitle">
                        Trabajador: <strong>{persona.nombre} {persona.apellido}</strong> ({persona.dni})
                    </p>

                    <div className="evento-fecha-observaciones" style={{ gridTemplateColumns: '1fr 1fr', borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
                        <div className="fecha-field">
                            <label>Fecha</label>
                            <input
                                type="date"
                                value={form.fecha}
                                onChange={(e) => setForm({ ...form, fecha: e.target.value })}
                            />
                        </div>
                        <div className="fecha-field">
                            <label>Motivo</label>
                            <input
                                type="text"
                                value={form.motivo}
                                onChange={(e) => setForm({ ...form, motivo: e.target.value })}
                                placeholder="Ej: Artículo en mal estado, talla incorrecta..."
                            />
                        </div>
                    </div>

                    <div className="observaciones-field" style={{ marginTop: '20px' }}>
                        <label>Observaciones</label>
                        <textarea
                            rows="3"
                            value={form.observaciones}
                            onChange={(e) => setForm({ ...form, observaciones: e.target.value })}
                            placeholder="Detalles adicionales..."
                        />
                    </div>

                    <div className="articulos-seleccionados" style={{ marginTop: '20px' }}>
                        <h4>{modo === 'devolucion' ? 'Artículo a devolver' : 'Artículo a cambiar'}</h4>
                        <select
                            className="input-talla"
                            value={form.id_articulo}
                            onChange={(e) => handleArticuloCambio(e.target.value)}
                        >
                            <option value="">Seleccione un artículo...</option>
                            {(evento.detalles || []).map(d => (
                                <option key={d.id || d.id_articulo} value={d.id || d.id_articulo}>
                                    {d.nombre} {d.talla_entregada ? `(Talla ${d.talla_entregada})` : ''} - Cantidad: {d.cantidad}
                                </option>
                            ))}
                        </select>
                    </div>

                    {modo === 'cambio' && articuloSeleccionado && (
                        <>
                            <div className="articulos-seleccionados" style={{ marginTop: '20px' }}>
                                <h4>Artículo nuevo</h4>
                                <select
                                    className="input-talla"
                                    value={form.id_articulo_nuevo}
                                    onChange={(e) => setForm({ ...form, id_articulo_nuevo: e.target.value, talla_nueva: '' })}
                                >
                                    <option value="">Seleccione artículo nuevo...</option>
                                    {articulosDisponibles.filter(a => a.id !== form.id_articulo).map(a => (
                                        <option key={a.id} value={a.id}>{a.nombre} ({a.categoria})</option>
                                    ))}
                                </select>
                            </div>

                            <div className="evento-fecha-observaciones" style={{ gridTemplateColumns: '1fr 1fr 1fr', borderTop: 'none', paddingTop: 0, marginTop: 0 }}>
                                <div className="fecha-field">
                                    <label>Talla anterior</label>
                                    <input type="text" value={form.talla_anterior} onChange={(e) => setForm({ ...form, talla_anterior: e.target.value })} />
                                </div>
                                {articuloNuevo?.requiere_talla ? (
                                    <div className="fecha-field">
                                        <label>Talla nueva</label>
                                        <select
                                            className="input-talla"
                                            value={form.talla_nueva}
                                            onChange={(e) => setForm({ ...form, talla_nueva: e.target.value })}
                                        >
                                            <option value="">Seleccione talla...</option>
                                            {(articuloNuevo.tallas || []).map(t => (
                                                <option key={t.id || t.talla} value={t.talla}>{t.talla}</option>
                                            ))}
                                        </select>
                                    </div>
                                ) : (
                                    <div className="fecha-field">
                                        <label>Talla nueva</label>
                                        <input type="text" value={form.talla_nueva} onChange={(e) => setForm({ ...form, talla_nueva: e.target.value })} placeholder="Opcional" />
                                    </div>
                                )}
                                <div className="fecha-field">
                                    <label>Cantidad</label>
                                    <input
                                        type="number"
                                        min="1"
                                        value={form.cantidad}
                                        onChange={(e) => setForm({ ...form, cantidad: parseInt(e.target.value) || 1 })}
                                    />
                                </div>
                            </div>
                        </>
                    )}

                    <div className="captura-container" style={{ marginTop: '24px' }}>
                        <div className="captura-seccion">
                            <h4>Foto</h4>
                            <div className="captura-opciones">
                                <input type="file" accept="image/*" onChange={handleSubirFoto} />
                                {fotoData && (
                                    <div className="foto-preview">
                                        <img src={fotoData} alt="Foto capturada" style={{ maxHeight: '160px' }} />
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="captura-seccion">
                            <h4>Firma</h4>
                            <canvas
                                ref={canvasRef}
                                width={380}
                                height={160}
                                className="firma-canvas"
                                onMouseDown={handleCanvasMouseDown}
                                onMouseMove={handleCanvasMouseMove}
                                onMouseUp={handleCanvasMouseUp}
                                onMouseLeave={handleCanvasMouseUp}
                                onTouchStart={handleCanvasMouseDown}
                                onTouchMove={handleCanvasMouseMove}
                                onTouchEnd={handleCanvasMouseUp}
                            />
                            <div className="firma-botones">
                                <button type="button" className="btn-secondary" onClick={limpiarFirma}>Limpiar</button>
                            </div>
                            {firmaData && <p className="firma-guardada">✅ Firma registrada</p>}
                        </div>
                    </div>

                    <div className="detalle-actions-footer" style={{ marginTop: '24px' }}>
                        <button type="button" className="btn-secondary" onClick={onClose} disabled={saving}>
                            Cancelar
                        </button>
                        <button type="button" className="btn-primary" onClick={handleSubmit} disabled={saving}>
                            {saving ? 'Guardando...' : (modo === 'devolucion' ? 'Registrar Devolución' : 'Registrar Cambio')}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}
